import { Injectable } from '@angular/core';

import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

import jsPDF from 'jspdf';

@Injectable({
  providedIn: 'root',
})
export class FileHandlerService {
  constructor() {}

  async checkForErrors(files: FileList) {
    if (!files || files.length === 0) {
      return;
    }

    try {
      return await this.filePreChecks(files);
    } catch (error) {
      console.warn(error);
      if (error.name === 'PasswordException') {
        throw Error('Your file is password protected. Please remove any password protection before uploading.');
      } else if (error.name === 'LowResolution') {
        throw Error('Your file has a lower than recommended resolution. Please upload a higher quality scan.');
      } else if (error.name === 'InvalidPDFException') {
        throw Error('We only support PDF files. Please ensure you have only uploaded PDF files.');
      } else {
        throw Error('Oops, we have run into a problem.');
      }
    }
  }

  private filePreChecks(files: FileList): Promise<Array<{ pageCount: number; file: File }>> {
    // All things we can detect before sending to docparser
    return Promise.all(
      Array.from(files).map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = async (e: any) => {
            try {
              const fileArrayBuffer = e.target.result;
              const pdf = await pdfjsLib.getDocument({ data: fileArrayBuffer }).promise;
              const numPages = pdf.numPages;
              const page = await pdf.getPage(Math.ceil(numPages / 2));
              const textContent = await page.getTextContent();
              const text: string = textContent.items.map((item: any) => item.str).join(' ');

              // Some PDFs just don't return text. We only want the explicitly broken ones with
              // strange encoded text
              if (text && this.isTextAbnormal(text.slice(0, 1000))) {
                const correctedName = 'fmt_' + file.name;
                file = await this.correctPDF(pdf, correctedName);
              }

              const viewport = page.getViewport({ scale: 1 });
              if (viewport.width < 300 || viewport.height < 450) {
                reject({ name: `LowResolution`, message: `${file.name} is too low resolution` });
              }

              resolve({ pageCount: numPages, file: file });
            } catch (error) {
              reject(error);
            }
          };
          reader.readAsArrayBuffer(file);
        }) as Promise<{ pageCount: number; file: any }>;
      }),
    );
  }

  private async correctPDF(pdf: pdfjsLib.PDFDocumentProxy, name: string): Promise<File> {
    const numPages = pdf.numPages;

    // Create a new PDF document using jsPDF
    const newPdf = new jsPDF();

    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 4 }); // Increase the scale for higher resolution

      // Render the page content onto a canvas
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: context, viewport }).promise;

      // Calculate the scaling factors to fit the rendered page to the new PDF document
      const pdfWidth = newPdf.internal.pageSize.getWidth();
      const pdfHeight = newPdf.internal.pageSize.getHeight();
      const widthRatio = pdfWidth / viewport.width;
      const heightRatio = pdfHeight / viewport.height;
      const scale = Math.min(widthRatio, heightRatio);

      // Add the rendered page image to the new PDF document with proper scaling
      const imgData = canvas.toDataURL('image/jpeg', 1.0); // Use JPEG format with maximum quality
      newPdf.addImage(imgData, 'JPEG', 0, 0, viewport.width * scale, viewport.height * scale);

      // Add a new page for the next iteration if it's not the last page
      if (pageNum < numPages) {
        newPdf.addPage();
      }
    }

    return new File([newPdf.output('blob')], name, { type: 'application/pdf' });
  }

  private isTextAbnormal(text: string): boolean {
    // Check for a high percentage of non-alphanumeric characters
    const nonAlphanumericRegex = /[^a-zA-Z0-9]/g;
    const nonAlphanumericCount = (text.match(nonAlphanumericRegex) || []).length;
    const nonAlphanumericPercentage = nonAlphanumericCount / text.length;

    if (nonAlphanumericPercentage > 0.5) {
      return true;
    }

    // Check for long runs of consecutive non-alphanumeric characters
    const consecutiveNonAlphanumericRegex = /[^a-zA-Z0-9\s]{10,}/;

    if (consecutiveNonAlphanumericRegex.test(text)) {
      return true;
    }

    return false;
  }
}
